import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "./TicketList.css";
import Navbar from "../../components/Navbar/Navbar";
import TicketDetailsModal from "../../components/TicketModal/TicketModal";
import RcaModal from "../../components/RcaModal/RcaModal";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const TicketList = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [rcaTicket, setRcaTicket] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState("created_date");
  const [sortOrder, setSortOrder] = useState("desc");

  const [filters, setFilters] = useState({
    customer: "",
    priority: "",
    status: "",
    date_from: "",
    date_to: "",
    is_closed: "",
    subject: "",
    issue_field: "",
    record_type_name: "",
    ticket_type: "",
    source: "",
    resolution_expected_from: "",
    search: "",
  });

  const [filterInputs, setFilterInputs] = useState({ ...filters });
  const [showFilters, setShowFilters] = useState(false);

  const gridRef = useRef(null);
  const debounceRef = useRef(null);

  const fetchTickets = useCallback(() => {
    setLoading(true);
    setError(null);

    const params = {
      page,
      page_size: pageSize,
      sort_by: sortBy,
      sort_order: sortOrder,
    };

    Object.entries(filters).forEach(([key, val]) => {
      if (val) params[key] = val;
    });

    axios
      .get("/tickets", { params })
      .then((response) => {
        const { data, pagination } = response.data;
        const parsed = (data || []).map((ticket) => ({
          ...ticket,
          created_date: ticket.created_date
            ? new Date(ticket.created_date)
            : null,
        }));
        setTickets(parsed);
        setTotalRows(pagination.total);
        setTotalPages(pagination.total_pages);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching tickets:", err);
        setError("Failed to fetch tickets. Please try again later.");
        setLoading(false);
      });
  }, [page, pageSize, sortBy, sortOrder, filters]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleSortChanged = (event) => {
    const colState = event.api.getColumnState();
    const sorted = colState.find((c) => c.sort);
    if (sorted) {
      setSortBy(sorted.colId);
      setSortOrder(sorted.sort);
    } else {
      setSortBy("created_date");
      setSortOrder("desc");
    }
    setPage(1);
  };

  const applyFilters = useCallback(() => {
    setFilters({ ...filterInputs });
    setPage(1);
  }, [filterInputs]);

  const clearFilters = useCallback(() => {
    const empty = {
      customer: "",
      priority: "",
      status: "",
      date_from: "",
      date_to: "",
      is_closed: "",
      subject: "",
      issue_field: "",
      record_type_name: "",
      ticket_type: "",
      source: "",
      resolution_expected_from: "",
      search: "",
    };
    setFilterInputs(empty);
    setFilters(empty);
    setPage(1);
  }, []);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setFilterInputs((prev) => ({ ...prev, search: val }));
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: val }));
      setPage(1);
    }, 400);
  };

  const columnDefs = [
    {
      headerName: "Source",
      field: "source",
      sortable: true,
      width: 90,
      cellRenderer: (params) => {
        const src = (params.value || "salesforce").toLowerCase();
        return (
          <span className={`source-badge source-badge-${src === "jira" ? "jira" : "sf"}`}>
            {src === "jira" ? "JIRA" : "SF"}
          </span>
        );
      },
    },
    {
      headerName: "Ticket #",
      field: "case_number",
      sortable: true,
      width: 150,
      cellStyle: { textAlign: "left", cursor: "pointer", color: "blue" },
      valueGetter: ({ data }) => {
        if (!data) return "";
        const isJira = (data.source || "").toLowerCase() === "jira";
        return isJira ? data.id : data.case_number || data.id;
      },
    },
    {
      headerName: "Customer Name",
      field: "account_alias_name",
      sortable: true,
      wrapText: true,
      autoHeight: true,
      width: 150,
      cellStyle: { textAlign: "left", lineHeight: "1.75rem" },
    },
    {
      headerName: "Subject",
      field: "subject",
      sortable: true,
      wrapText: true,
      autoHeight: true,
      width: 250,
      cellStyle: { textAlign: "left", lineHeight: "1.75rem" },
    },
    {
      headerName: "Priority",
      field: "priority",
      sortable: true,
      width: 100,
      cellStyle: { textAlign: "left" },
    },
    {
      headerName: "RCA",
      field: "rca",
      width: 80,
      sortable: false,
      cellRenderer: (params) => {
        const isJira = (params.data.source || "").toLowerCase() === "jira";
        if (isJira) {
          if (!params.data.confluence_rca_link) return <span style={{ color: "#999" }}>—</span>;
          return (
            <a
              href={params.data.confluence_rca_link}
              target="_blank"
              rel="noopener noreferrer"
              className="rca-link-btn"
              title="View Confluence RCA"
            >
              RCA
            </a>
          );
        }
        if (!params.data.case_number) return null;
        return (
          <button
            style={{
              background: "none",
              border: "none",
              color: "#0c4280",
              fontWeight: 600,
              fontSize: "0.85rem",
              cursor: "pointer",
              textDecoration: "underline",
              padding: "4px 2px",
            }}
            onClick={() =>
              setRcaTicket({
                caseNumber: params.data.case_number,
                subject: params.data.subject,
              })
            }
          >
            RCA
          </button>
        );
      },
    },
    {
      headerName: "Chat",
      field: "gchat_link",
      width: 80,
      sortable: false,
      cellRenderer: (params) => {
        if (!params.value) return <span style={{ color: "#999" }}>—</span>;
        return (
          <a
            href={params.value}
            target="_blank"
            rel="noopener noreferrer"
            className="rca-link-btn rca-link-chat"
            title="View Google Chat thread"
          >
            Chat
          </a>
        );
      },
    },
    {
      headerName: "Created Date",
      field: "created_date",
      sortable: true,
      width: 150,
      cellStyle: { textAlign: "left" },
      valueFormatter: ({ value }) =>
        value
          ? new Date(value).toLocaleString("en-US", {
              weekday: "short",
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              timeZoneName: "short",
            })
          : "—",
    },
    {
      headerName: "Resolution Time",
      field: "resolution_time",
      sortable: false,
      width: 160,
      cellStyle: { textAlign: "left" },
      valueGetter: ({ data }) => {
        if (!data.date_resolved || !data.created_date) return null;
        const created = new Date(data.created_date);
        const resolved = new Date(data.date_resolved);
        const diffMs = resolved - created;
        if (diffMs < 0) return null;
        return diffMs;
      },
      valueFormatter: ({ value }) => {
        if (value == null) return "—";
        const totalMinutes = Math.floor(value / 60000);
        const days = Math.floor(totalMinutes / 1440);
        const hours = Math.floor((totalMinutes % 1440) / 60);
        const minutes = totalMinutes % 60;
        if (days > 0) return `${days}d ${hours}h ${minutes}m`;
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
      },
      comparator: (a, b) => {
        if (a == null && b == null) return 0;
        if (a == null) return 1;
        if (b == null) return -1;
        return a - b;
      },
    },
    {
      headerName: "Is Closed",
      field: "is_closed",
      sortable: true,
      width: 100,
      valueFormatter: ({ value }) => (value ? "Yes" : "No"),
      cellStyle: { textAlign: "left" },
    },
    {
      headerName: "Status",
      field: "status",
      sortable: true,
      width: 150,
      cellStyle: { textAlign: "left" },
    },
    {
      headerName: "Resolution From",
      field: "resolution_expected_from",
      sortable: true,
      width: 140,
      cellStyle: { textAlign: "left" },
      cellRenderer: (params) => {
        if (!params.value) return <span style={{ color: "#999" }}>—</span>;
        const color = params.value === "Client" ? "#d97706" : "#059669";
        return <span style={{ color, fontWeight: 600 }}>{params.value}</span>;
      },
    },
    {
      headerName: "CSAT Score",
      field: "csat_score",
      sortable: true,
      width: 120,
      cellStyle: { textAlign: "left" },
    },
    {
      headerName: "Root Cause",
      field: "root_cause_summary",
      sortable: true,
      wrapText: true,
      autoHeight: true,
      width: 300,
      cellStyle: { textAlign: "left", lineHeight: "1.5rem" },
      cellRenderer: (params) => {
        if (!params.value) return <span style={{ color: "#999" }}>—</span>;
        const text = params.value;
        if (text.length <= 100) return text;
        return <span title={text}>{text.substring(0, 100)}...</span>;
      },
    },
    {
      headerName: "Actions",
      field: "actions",
      width: 130,
      sortable: false,
      cellRenderer: (params) => (
        <button
          className="text-sm bg-green-500 rounded-md py-2 px-2"
          onClick={() => handleOpenTicketDetails(params.data.id)}
        >
          View Details
        </button>
      ),
    },
  ];

  const onCellClicked = (params) => {
    if (params.column.colId === "case_number") {
      const isJira = (params.data.source || "").toLowerCase() === "jira";
      const url = isJira
        ? `https://exotel.atlassian.net/browse/${params.data.id}`
        : `https://ameyo.lightning.force.com/lightning/r/Case/${params.data.id}/view`;
      window.open(url, "_blank");
    }
  };

  const handleOpenTicketDetails = (ticketId) => {
    setSelectedTicketId(ticketId);
  };

  const handleCloseModal = () => {
    setSelectedTicketId(null);
  };

  const startRow = totalRows === 0 ? 0 : (page - 1) * pageSize + 1;
  const endRow = Math.min(page * pageSize, totalRows);

  return (
    <div>
      <Navbar />
      <div className="container mx-auto px-4 py-2">
        <h1 className="text-2xl font-bold mb-3">Incidents & Tickets</h1>

        {/* Global search + filter toggle */}
        <div className="ticket-toolbar">
          <input
            type="text"
            className="ticket-search-input"
            placeholder="Search tickets by case number, subject, or customer..."
            value={filterInputs.search}
            onChange={handleSearchChange}
          />
          <button
            className={`ncdnts-toggle ${filters.source === "jira" ? "ncdnts-toggle--active" : ""}`}
            onClick={() => {
              const next = filters.source === "jira" ? "" : "jira";
              setFilterInputs((p) => ({ ...p, source: next }));
              setFilters((p) => ({ ...p, source: next }));
              setPage(1);
            }}
          >
            NCDNTS Incidents
          </button>
          <button
            className="ticket-filter-toggle"
            onClick={() => setShowFilters((v) => !v)}
          >
            {showFilters ? "Hide Filters" : "Show Filters"}
          </button>
        </div>

        {/* Collapsible filter panel */}
        {showFilters && (
          <div className="ticket-filter-panel">
            <div className="filter-row">
              <label>
                Customer
                <input
                  type="text"
                  value={filterInputs.customer}
                  onChange={(e) =>
                    setFilterInputs((p) => ({ ...p, customer: e.target.value }))
                  }
                  placeholder="Customer name..."
                />
              </label>
              <label>
                Priority
                <input
                  type="text"
                  value={filterInputs.priority}
                  onChange={(e) =>
                    setFilterInputs((p) => ({ ...p, priority: e.target.value }))
                  }
                  placeholder="e.g. P1,P2"
                />
              </label>
              <label>
                Status
                <input
                  type="text"
                  value={filterInputs.status}
                  onChange={(e) =>
                    setFilterInputs((p) => ({ ...p, status: e.target.value }))
                  }
                  placeholder="e.g. Open,Closed"
                />
              </label>
              <label>
                Subject
                <input
                  type="text"
                  value={filterInputs.subject}
                  onChange={(e) =>
                    setFilterInputs((p) => ({ ...p, subject: e.target.value }))
                  }
                  placeholder="Search subject..."
                />
              </label>
            </div>
            <div className="filter-row">
              <label>
                Date From
                <input
                  type="date"
                  value={filterInputs.date_from}
                  onChange={(e) =>
                    setFilterInputs((p) => ({
                      ...p,
                      date_from: e.target.value,
                    }))
                  }
                />
              </label>
              <label>
                Date To
                <input
                  type="date"
                  value={filterInputs.date_to}
                  onChange={(e) =>
                    setFilterInputs((p) => ({
                      ...p,
                      date_to: e.target.value,
                    }))
                  }
                />
              </label>
              <label>
                Is Closed
                <select
                  value={filterInputs.is_closed}
                  onChange={(e) =>
                    setFilterInputs((p) => ({
                      ...p,
                      is_closed: e.target.value,
                    }))
                  }
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </label>
            </div>
            <div className="filter-row">
              <label>
                Source
                <select
                  value={filterInputs.source}
                  onChange={(e) =>
                    setFilterInputs((p) => ({
                      ...p,
                      source: e.target.value,
                    }))
                  }
                >
                  <option value="">All</option>
                  <option value="salesforce">Salesforce</option>
                  <option value="jira">JIRA</option>
                </select>
              </label>
              <label>
                Resolution From
                <select
                  value={filterInputs.resolution_expected_from}
                  onChange={(e) =>
                    setFilterInputs((p) => ({
                      ...p,
                      resolution_expected_from: e.target.value,
                    }))
                  }
                >
                  <option value="">All</option>
                  <option value="Exotel">Exotel</option>
                  <option value="Client">Client</option>
                </select>
              </label>
              <div className="filter-actions">
                <button className="btn-apply" onClick={applyFilters}>
                  Apply Filters
                </button>
                <button className="btn-clear" onClick={clearFilters}>
                  Clear All
                </button>
              </div>
            </div>
          </div>
        )}

        {error && <p className="text-red-600">{error}</p>}

        <div
          className="ag-theme-quartz"
          style={{ height: 520, width: "100%" }}
        >
          <AgGridReact
            ref={gridRef}
            rowData={tickets}
            columnDefs={columnDefs}
            pagination={false}
            defaultColDef={{
              minWidth: 100,
              resizable: true,
              sortable: true,
              filter: false,
            }}
            onSortChanged={handleSortChanged}
            onCellClicked={onCellClicked}
            suppressLoadingOverlay={!loading}
            loading={loading}
          />
        </div>

        {/* Custom pagination controls */}
        <div className="ticket-pagination">
          <div className="pagination-info">
            Showing {startRow}–{endRow} of {totalRows} tickets
          </div>
          <div className="pagination-controls">
            <label className="page-size-label">
              Rows per page:
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {PAGE_SIZE_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <button
              className="page-btn"
              disabled={page <= 1}
              onClick={() => setPage(1)}
              title="First page"
            >
              &laquo;
            </button>
            <button
              className="page-btn"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              title="Previous page"
            >
              &lsaquo;
            </button>
            <span className="page-indicator">
              Page {page} of {totalPages}
            </span>
            <button
              className="page-btn"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              title="Next page"
            >
              &rsaquo;
            </button>
            <button
              className="page-btn"
              disabled={page >= totalPages}
              onClick={() => setPage(totalPages)}
              title="Last page"
            >
              &raquo;
            </button>
          </div>
        </div>

        <TicketDetailsModal
          isOpen={selectedTicketId !== null}
          onClose={handleCloseModal}
          ticketId={selectedTicketId}
        />
        <RcaModal
          isOpen={rcaTicket !== null}
          onClose={() => setRcaTicket(null)}
          caseNumber={rcaTicket?.caseNumber}
          ticketSubject={rcaTicket?.subject}
        />
      </div>
    </div>
  );
};

export default TicketList;
